'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ReadMoreButton } from '../common/ReadMoreButton';
import LogoOnDark from './assets/primary-logotype-on-dark.png';
import ComplianceLines from './assets/compliance-lines.svg';
import aicpaSoc from './assets/aicpa-soc.webp';
import { Route } from 'next';
import { usePathname } from 'next/navigation';
import { socialData } from '@/utilities/data';

const footerLinks: {
  title: string;
  links: {
    text: string;
    href: Route;
  }[];
}[] = [
  {
    title: 'Why Primary',
    links: [
      { text: 'About', href: '/what-is-primary' },
      { text: 'Data Security', href: '/security' },
      { text: 'Data Visibility', href: '/visibility' },
      { text: 'Data Control', href: '/control' },
    ],
  },
  {
    title: 'Platform',
    links: [
      { text: 'Deploying Primary', href: '/making-the-switch' },
      { text: 'Enterprise Control Plane', href: '/enterprise-control-plane' },
      // { text: 'Private Browsing', href: '/private-browsing' },
      { text: 'Zero Trust', href: '/zero-trust-security' },
    ],
  },
  {
    title: 'Company',
    links: [
      { text: 'About Primary', href: '/about' },
      { text: 'Our Team', href: '/team' },
      { text: 'Sales & Support Contact', href: '/contact' },
    ],
  },
];

type FooterCta = {
  heading: string;
  lead: string;
  buttonText: string;
  buttonHref: Route;
};

// Default CTA shown in the footer. Any page can override it below.
const defaultFooterCta: FooterCta = {
  heading: 'See Primary in action.',
  lead: 'Primary enables what your people and AI agents can see, decide, and do.',
  buttonText: 'Schedule a conversation',
  buttonHref: '/contact',
};

// Per-page overrides, keyed by pathname. Anything omitted falls back to the default.
const footerCtaOverrides: Partial<Record<string, FooterCta>> = {
  '/control': {
    heading: 'Connect with Us',
    lead: 'The Data Control Plane is designed to help enterprises safely govern how human and agentic actors access, use, and act upon sensitive data.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/visibility': {
    heading: 'Connect with Us',
    lead: 'The Data Control Plane is designed to help enterprises securely scale human and agentic activity with complete visibility across every data interaction.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/security': {
    heading: 'Connect with Us',
    lead: 'Primary’s Zero Trust architecture provides the secure foundation enterprises need to rely on the Data Control Plane across every human, agentic, and application-driven interaction.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/zero-trust-architecture': {
    heading: 'Connect with Us',
    lead: 'Primary’s Zero Trust architecture provides the secure foundation required to govern human and agentic activity across every identity, workflow, application, and data interaction.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/enterprise-control-plane': {
    heading: 'Start Your Free Trial Now',
    lead: 'Govern how work moves across users, agents, applications, and data through one enterprise control plane—with the auditable record to prove it.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/what-is-primary': {
    heading: 'Connect with Us',
    lead: 'Primary is the Data Control Plane for operating a secure, observable, and governed workforce of human and agentic actors.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/making-the-switch': {
    heading: 'Connect with Us',
    lead: 'Primary is the Data Control Plane designed to help enterprises move from disconnected agent experimentation to secure, observable, and governed human and agentic operations.',
    buttonText: 'Get started',
    buttonHref: '/contact',
  },
  '/enterprise-workspace': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Workspace Control Plane designed to help enterprises govern, secure, and scale the next generation of human and agentic work.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/unified-governance': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Enterprise Control Plane designed to help organizations govern and scale the new generation of work.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/ai-classification': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Unified Control Plane designed to transform telemetry into intelligence—and intelligence into policies that reduce risk.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/identity-governance': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Enterprise Control Plane designed to govern every human and non-human identity across the new generation of work.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/attribute-based-access-control': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Enterprise Control Plane designed to deliver real-time, attribute-based control across the new generation of work.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/ai-cost-governance': {
    heading: 'Start Your Free Trial Now',
    lead: 'The Enterprise Control Plane designed to govern and optimize the economics of the new generation of work.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
  '/agent-control-planes': {
    heading: 'Build a More Governed Agentic Enterprise',
    lead: 'Deploy, coordinate and monitor AI agents through a unified control layer designed for enterprise scale. Establish consistent policies. Connect agents across systems. Observe every critical action. Introduce new capabilities without losing control.',
    buttonText: 'Get Started',
    buttonHref: '/contact',
  },
};

const currentYear = new Date().getFullYear();

const footerDocsLinks: {
  text: string;
  href: Route;
}[] = [
  { text: `Primary® ${currentYear}`, href: '/' },
  { text: 'Privacy Policy', href: '/privacy-policy' },
];

const socialLinks: {
  label: string;
  href: string;
  icon: React.ReactNode;
}[] = [
  {
    label: socialData.linkedin.label,
    href: socialData.linkedin.href,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
        className="h-5 w-5"
      >
        <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
      </svg>
    ),
  },
  {
    label: socialData.x.label,
    href: socialData.x.href,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
        className="h-5 w-5"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
      </svg>
    ),
  },
];

/**
 * The SOC badge panel from the footer design. The blue line-art is a single
 * oversized vector hung off the card's top-left corner and clipped by it —
 * geometry lifted from Figma (node 10308:18855).
 *
 * The 288px cap is the design's two-line measure: the longer line runs 251px
 * at 20px, plus 16px of padding and 1px of border either side.
 */
const ComplianceCard = () => (
  <div className="relative flex w-full max-w-[288px] flex-col justify-center gap-4 overflow-hidden rounded-lg border border-[#3576CD] p-4">
    <ComplianceLines
      aria-hidden
      className="pointer-events-none absolute h-[484px] w-[433px] max-w-none -translate-x-[68px] -translate-y-[47px] rotate-[-142.83deg]"
      style={{ left: 0, top: 0 }}
    />
    <Image
      src={aicpaSoc}
      alt=""
      aria-hidden
      className="relative size-25 shrink-0"
    />
    <p className="text-l relative font-normal text-white">
      Your Data. Protected with
      <br />
      Industry-Leading Standards
    </p>
  </div>
);

export default function Footer({ notFound }: { notFound?: boolean }) {
  const pathname = usePathname();

  // Everything under /contact carries its own contact CTA already.
  const isContactPage = pathname === '/contact' || pathname.startsWith('/contact/');
  // Pages that opt out of the "See Primary in action" CTA entirely.
  // The Black Hat page used to opt out because it carried its own registration
  // CTAs. Those came out once the event finished, so it takes the global one.
  const hidesCta =
    isContactPage || pathname === '/' || pathname === '/partner-program';
  // The footer is global; each page can override the CTA via footerCtaOverrides.
  const cta = footerCtaOverrides[pathname] ?? defaultFooterCta;
  return (
    <footer>
      {!notFound && !hidesCta && (
        <div className="border-t-primary-default/20 bg-background border-t-2">
          <div className="footer-content-container">
            <div className="footer-content-link" />
            <div>
              <h2 className="text-h4">{cta.heading}</h2>
              <p className="text-default my-6 max-w-xl text-lg font-light">
                {cta.lead}
              </p>
              <ReadMoreButton
                href={cta.buttonHref}
                text={cta.buttonText}
                className="inline-block"
                slim
              />
            </div>
          </div>
        </div>
      )}
      <div className="bg-black text-white">
        {/* A tighter column gap than the CTA band above: the design gives the
            link stack the lion's share of the row, roughly three quarters. */}
        <div className="footer-content-container pb-6 max-md:flex-col md:gap-26 sm:pb-10 xl:gap-26">
          {/* No wider than the SOC panel it holds — the design spends the rest
              of the row on the links. The logo sits at the top of the column
              and the panel at its foot; this column, not the link stack, is
              what sets the footer's height in Figma — 360px, which puts the
              panel's foot 40px above the bottom edge and the bar at 440px. */}
          <div className="flex shrink-0 flex-col justify-between gap-10 md:min-h-90 md:w-72">
            {/* The navigation logotype with the wordmark flipped to white, so
                the mark carries exactly the same gradient in both places —
                logo.svg can't stand in for it, since Figma's angular gradient
                degrades to a flat two-stop radial there. A plain white version
                is only used where the logotype sits on artwork. */}
            <Link href="/" className="w-44" title="Homepage">
              <Image
                src={LogoOnDark}
                alt="Logo"
                className="block h-auto w-full"
              />
            </Link>
            <ComplianceCard />
          </div>
          <div className="flex grow flex-col items-start">
            <div className="xs:grid-cols-3 mb-6 grid w-full gap-x-4 gap-y-10 border-y border-white/20 py-6 text-sm sm:mb-10 sm:gap-10 sm:py-10">
              {footerLinks.map(({ title, links }) => (
                <div key={title}>
                  <h3 className="xs:mb-4 mb-2">{title}</h3>
                  <ul className="xs:gap-y-4 flex flex-col gap-y-2">
                    {links.map(({ text, href }) => (
                      <li key={text}>
                        <Link
                          href={href || '/'}
                          className="text-white/80 transition-colors hover:text-white"
                        >
                          {text}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex w-full items-center justify-between gap-x-4">
              <ul className="flex grow flex-wrap items-center gap-x-4 gap-y-1 text-sm font-light sm:gap-x-10">
                {footerDocsLinks.map(({ text, href }) => (
                  <li key={text}>
                    <Link
                      href={href}
                      className="text-white/80 transition-colors hover:text-white"
                    >
                      {text}
                    </Link>
                  </li>
                ))}
              </ul>
              <nav aria-label="Social media" className="-mr-2 shrink-0">
                <ul className="flex items-center gap-1">
                  {socialLinks.map(({ label, href, icon }) => (
                    <li key={label}>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="inline-flex rounded p-2 text-white/80 transition-colors hover:text-white"
                      >
                        {icon}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
